import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { collection, doc, getCountFromServer, getDoc, getDocs, onSnapshot, orderBy, query, where, limit as fsLimit } from 'firebase/firestore';
import {
  ArrowPathIcon, ArrowUpTrayIcon, CheckIcon, ChevronRightIcon, PaperAirplaneIcon, PlusIcon, RocketLaunchIcon,
  SparklesIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import PageHeader from '../../components/ui/PageHeader';
import SectionCard from '../../components/ui/SectionCard';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/ui/EmptyState';
import { ADMIN_PAGE_WIDTH, cx } from '../../components/ui/utils';
import { db } from '../../firebase';
import {
  createMarketingCampaign, updateMarketingCampaign, launchMarketingCampaign, processMarketingCampaign,
  previewColdLeadCampaign, prepareColdLeadCampaign, previewMarketingImport,
  addContactsToCampaign, uploadMarketingAsset, sendMarketingCampaignTest,
  generateMarketingCampaignDraft, getMarketingCampaignAudience, listApprovedWhatsAppTemplates,
} from '../../services/marketingApi';
import { listPublishedMarketingLandings } from '../../services/searchConsoleApi';

const AUDIENCE_LABELS = {
  new_leads: 'Leads nuevos/en proceso', engaged: 'Ya interactuaron (marketing_engaged)',
  remarketing: 'Inactivos (remarketing)', manual_import: 'Lista importada (CSV)', cold_leads: 'Leads fríos',
  campaign_remarketing: 'Reenvío a quien no completó (de otra campaña)',
  clients_segment: 'Clientes existentes (por línea de producto)',
};

const CLIENT_PRODUCT_LINE_LABELS = {
  all: 'Todas las líneas', erp: 'CRM/ERP (Book Me / Clinic 360)', restaurante: 'Restaurantes (Meso 360)',
};

const SOLUTION_OPTIONS = {
  meso360: 'Meso 360 — restaurantes',
  medic360: 'Medic 360 — clínicas y bienestar',
  master360: 'Master 360 — negocios y operación',
};

const REMARKETING_COHORT_LABELS = {
  opened_no_click: 'Abrieron el email pero no le dieron clic al cuestionario',
  wizard_incomplete: 'Empezaron el cuestionario pero no lo terminaron',
};

const AUTOMATION_TRIGGER_LABELS = {
  opened_no_click: 'Abre el email, pero no hace clic',
  clicked_no_qualification: 'Hace clic, pero no completa la calificación',
  landing_incomplete: 'Inicia la landing/wizard y la abandona',
  qualification_completed: 'Completa el cuestionario',
  high_intent_lead: 'Se detecta interés alto',
  email_reply: 'Responde al email',
  no_interaction: 'No interactúa con la campaña',
};
const AUTOMATION_ACTION_LABELS = {
  follow_up_email: 'Email de seguimiento',
  create_team_task: 'Crear tarea al equipo',
  notify_owner: 'Notificar responsable',
  pause_nurture: 'Pausar seguimiento automático',
  create_remarketing_audience: 'Preparar audiencia de remarketing',
  suggest_whatsapp_follow_up: 'Sugerir seguimiento por WhatsApp',
};
const automationTriggerLabel = (value) => AUTOMATION_TRIGGER_LABELS[value] || value;
const automationActionLabel = (value) => AUTOMATION_ACTION_LABELS[value] || value;

const CAMPAIGN_STATUS_TONE = { draft: 'slate', ready: 'blue', running: 'amber', paused: 'rose', completed: 'emerald', archived: 'slate' };
const ENROLLMENT_STATUS_TONE = {
  queued: 'slate', processing: 'amber', sent: 'blue', delivered: 'blue', opened: 'violet', clicked: 'violet',
  responded: 'emerald', paused: 'slate', suppressed: 'rose', failed: 'rose', converted: 'emerald',
};

const emptyDraft = () => ({
  name: '', subject: '', preheader: '', body: '', ctaLabel: 'Cuéntanos qué necesitas', ctaUrl: '',
  landingSlug: '',
  objective: 'lead', niche: '', pain: '', offer: '', ctaType: 'wizard', channels: ['email'], whatsappTemplateSid: '',
  automationPlan: [],
  audience: 'new_leads', interestWizard: true, importCsv: '', importFileName: '', consentAllRows: false,
  sourceCampaignId: '', remarketingCohort: 'opened_no_click', minHoursSince: 24, image: null,
  clientProductLine: 'all', htmlBody: '', solutionId: '', businessVertical: '',
});

// El navegador no puede subir directo a Storage con este rol (Storage no
// puede leer el rol desde Firestore, a diferencia de las reglas de
// Firestore) — por eso la imagen viaja al backend y es marketingUploadAsset,
// ya con permisos de servidor, quien la sube y devuelve una URL firmada que
// cualquiera puede abrir sin sesión.
function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(reader.error || new Error('No se pudo leer el archivo.'));
    reader.readAsDataURL(file);
  });
}

async function uploadCampaignImage(file) {
  const dataUrl = await readFileAsDataUrl(file);
  const { asset } = await uploadMarketingAsset({ dataUrl, mimeType: file.type, fileName: file.name, alt: '' });
  return { id: asset.id, url: asset.url, storagePath: asset.storagePath, alt: asset.alt || '' };
}

const IMPORT_ROW_LIMIT = 2000;
const HTML_FILE_MAX_BYTES = 750 * 1024;

function htmlToPlainText(html) {
  if (typeof window === 'undefined' || !html) return '';
  const documentBody = new DOMParser().parseFromString(String(html), 'text/html').body;
  return String(documentBody?.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 20000);
}

function findLocalHtmlImageSources(html) {
  if (typeof window === 'undefined' || !html) return [];
  const images = Array.from(new DOMParser().parseFromString(String(html), 'text/html').querySelectorAll('img[src]'));
  return [...new Set(images.map((image) => String(image.getAttribute('src') || '').trim()).filter((source) => source
    && !/^(https?:|data:|cid:|#)/i.test(source)))];
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const emptyColdDraft = () => ({
  name: 'Reactivación — Diagnóstico Operacional BBTS', subject: 'Identifiquemos qué puede mejorar en su operación',
  body: 'Responda unas preguntas cortas para identificar el punto de partida adecuado para su operación.',
  ctaLabel: 'Cuéntanos qué necesitas', ctaUrl: '', newLeadDays: 3, inactiveDays: 7, minDaysBetweenCampaigns: 30,
});

// Acepta tanto un CSV completo (email,nombre,empresa,teléfono,línea,consentimiento)
// como una exportación de solo emails (una columna, con o sin encabezado) —
// que es lo típico al bajar una lista de Mautic.
function parseImportCsv(raw, { consentAllRows = false } = {}) {
  let lines = String(raw || '').split('\n').map((line) => line.trim()).filter(Boolean);
  // La primera línea sin "@" se asume encabezado (ej. "email,nombre,...").
  if (lines.length && !lines[0].includes('@')) lines = lines.slice(1);

  const seen = new Set();
  let duplicatesRemoved = 0;
  const totalRaw = lines.length;
  const truncated = lines.length > IMPORT_ROW_LIMIT;
  const rows = [];
  lines.slice(0, IMPORT_ROW_LIMIT).forEach((line, index) => {
    const [email, name, businessName, phone, productLine, consent] = line.split(',').map((cell) => (cell || '').trim());
    const key = (email || '').toLowerCase();
    if (key && seen.has(key)) { duplicatesRemoved += 1; return; }
    if (key) seen.add(key);
    rows.push({
      rowNumber: index + 1, email, name, businessName, phone, productLine,
      emailMarketingConsent: consentAllRows || /^(si|sí|s|yes|y|true|1)$/i.test(consent || ''),
    });
  });
  return { rows, duplicatesRemoved, totalRaw, truncated };
}

export default function AdminMarketingCampaigns() {
  const location = useLocation();
  const [campaigns, setCampaigns] = useState([]);
  const [publishedLandings, setPublishedLandings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(null); // null | 'standard' | 'cold'
  const [draft, setDraft] = useState(emptyDraft());
  const [coldDraft, setColdDraft] = useState(emptyColdDraft());
  const [coldPreview, setColdPreview] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [enrollments, setEnrollments] = useState([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(true);
  const [audienceMembers, setAudienceMembers] = useState([]);
  const [audienceEligibilitySummary, setAudienceEligibilitySummary] = useState({});
  const [audienceLoading, setAudienceLoading] = useState(false);
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [addContactsOpen, setAddContactsOpen] = useState(false);
  const [addContactsDraft, setAddContactsDraft] = useState({ importCsv: '', importFileName: '', consentAllRows: false });
  const [addContactsPreview, setAddContactsPreview] = useState(null);
  const [addingContacts, setAddingContacts] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  const closeWorkspace = () => {
    setSelectedId(null);
    setCreating(null);
    setAddContactsOpen(false);
    setError('');
  };

  // Lista de campañas en vivo — sentCount/status/responseCount se actualizan
  // solos según manda el drenado automático, sin recargar la página.
  useEffect(() => {
    const q = query(collection(db, 'marketing_campaigns'), orderBy('updatedAt', 'desc'), fsLimit(100));
    const unsub = onSnapshot(q, (snap) => {
      setCampaigns(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { setError(err.message || 'No se pudieron cargar las campañas.'); setLoading(false); });
    return unsub;
  }, []);

  useEffect(() => {
    const campaignId = new URLSearchParams(location.search).get('campaignId');
    if (campaignId && campaigns.some((campaign) => campaign.id === campaignId)) {
      setSelectedId(campaignId);
      setCreating(null);
    }
  }, [campaigns, location.search]);

  // Una campaña selecciona una landing publicada una sola vez. El envío
  // resuelve después el enlace individual por contacto, sin copiar URLs.
  useEffect(() => { let active = true; listPublishedMarketingLandings().then((result) => { if (active) setPublishedLandings(result.landings || []); }).catch((err) => { if (active) setError(err.message || 'No se pudieron cargar las landings publicadas.'); }); return () => { active = false; }; }, []);

  const selected = useMemo(() => campaigns.find((c) => c.id === selectedId) || null, [campaigns, selectedId]);

  const campaignToDraft = (campaign) => ({
    ...emptyDraft(),
    name: campaign.name || '', subject: campaign.subject || '', preheader: campaign.preheader || '',
    body: campaign.textBody || campaign.body || '', htmlBody: campaign.htmlBody || '',
    ctaLabel: campaign.ctaLabel || 'Cuéntanos qué necesitas', ctaUrl: campaign.ctaUrl || '', landingSlug: campaign.landingSlug || '',
    audience: campaign.audience || 'new_leads', interestWizard: campaign.interestWizard?.enabled === true,
    image: campaign.image || null, clientProductLine: campaign.clientProductLine || 'all',
    solutionId: campaign.solutionId || '', businessVertical: campaign.businessVertical || '',
    sourceCampaignId: campaign.sourceCampaignId || '', remarketingCohort: campaign.remarketingCohort || 'opened_no_click',
    minHoursSince: campaign.remarketingMinHoursSince || 24,
    automationPlan: Array.isArray(campaign.automationPlan) ? campaign.automationPlan : [],
    objective: campaign.campaignBrief?.objective || 'lead', niche: campaign.campaignBrief?.niche || '',
    pain: campaign.campaignBrief?.pain || '', offer: campaign.campaignBrief?.offer || '',
    ctaType: campaign.campaignBrief?.ctaType || 'wizard', channels: campaign.campaignBrief?.channels || ['email'],
    whatsappTemplateSid: campaign.whatsappTemplate?.id || '',
    // La lista ya existe dentro de la campaña; al editar solo cambia diseño y
    // mensaje, no se vuelve a importar ni se pide consentimento otra vez.
    consentAllRows: true,
  });

  // Resultados por campaña en vivo — así se ve en tiempo real quién abre,
  // hace clic o completa el cuestionario a medida que va pasando.
  useEffect(() => {
    if (!selectedId) { setEnrollments([]); return undefined; }
    setEnrollmentsLoading(true);
    const q = query(collection(db, 'marketing_campaigns', selectedId, 'enrollments'), orderBy('updatedAt', 'desc'), fsLimit(200));
    const unsub = onSnapshot(q, (snap) => {
      setEnrollments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setEnrollmentsLoading(false);
    }, (err) => { setError(err.message || 'No se pudieron cargar los resultados.'); setEnrollmentsLoading(false); });
    return unsub;
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) { setAudienceMembers([]); setAudienceEligibilitySummary({}); return undefined; }
    let active = true;
    setAudienceLoading(true);
    getMarketingCampaignAudience(selectedId)
      .then((result) => { if (active) { setAudienceMembers(result.memberships || []); setAudienceEligibilitySummary(result.eligibilitySummary || {}); } })
      .catch((err) => { if (active) setError(err.message || 'No se pudo cargar la audiencia.'); })
      .finally(() => { if (active) setAudienceLoading(false); });
    return () => { active = false; };
  }, [selectedId]);

  const loadWhatsappTemplates = useCallback(() => {
    return listApprovedWhatsAppTemplates()
      .then((result) => setWhatsappTemplates(result.templates || []))
      .catch((err) => setError(err.message || 'No se pudieron cargar las plantillas de WhatsApp.'));
  }, []);

  useEffect(() => { loadWhatsappTemplates(); }, [loadWhatsappTemplates]);

  const select = (campaignId) => { setSelectedId(campaignId); setCreating(null); setAddContactsOpen(false); };

  const startCreate = () => { setCreating('standard'); setSelectedId(null); setDraft(emptyDraft()); setImportPreview(null); setNotice(''); setError(''); };
  const startEdit = (campaign) => { setCreating('edit'); setDraft(campaignToDraft(campaign)); setImportPreview(null); setNotice(''); setError(''); };
  const cancelCreate = () => { setCreating(null); };

  const handleDraftImageFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImageUploading(true); setError('');
    try {
      const image = await uploadCampaignImage(file);
      setDraft((c) => ({ ...c, image }));
    } catch (e) { setError(e.message || 'No se pudo subir la imagen.'); }
    finally { setImageUploading(false); }
  };
  const clearDraftImage = () => setDraft((c) => ({ ...c, image: null }));

  const runImportPreview = async () => {
    if (draft.audience === 'manual_import' && !draft.consentAllRows) { setError('Confirma el consentimiento de la lista antes de validar.'); return; }
    const { rows, duplicatesRemoved, totalRaw, truncated } = parseImportCsv(draft.importCsv, { consentAllRows: draft.consentAllRows });
    if (!rows.length) { setError('Pega o sube al menos un email.'); return; }
    setError('');
    try {
      const data = await previewMarketingImport(rows);
      setImportPreview({ ...data, duplicatesRemoved, totalRaw, truncated });
    } catch (e) { setError(e.message || 'No se pudo validar el CSV.'); }
  };

  const handleImportFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setDraft((c) => ({ ...c, importCsv: String(reader.result || ''), importFileName: file.name }));
      setImportPreview(null);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const runColdPreview = async () => {
    try {
      const { newLeadDays, inactiveDays, minDaysBetweenCampaigns } = coldDraft;
      setColdPreview(await previewColdLeadCampaign({ newLeadDays, inactiveDays, minDaysBetweenCampaigns }));
    } catch (e) { setError(e.message || 'No se pudo calcular la audiencia fría.'); }
  };

  const submitCreate = async () => {
    if (!draft.name.trim()) { setError('Escribe un nombre para la campaña.'); return; }
    if (draft.audience === 'manual_import' && !draft.consentAllRows) { setError('Confirma el consentimiento de la lista antes de crear la campaña.'); return; }
    if (draft.audience === 'campaign_remarketing' && !draft.sourceCampaignId) { setError('Elige de qué campaña reenviar.'); return; }
    setSaving(true); setError('');
    try {
      const payload = {
        name: draft.name, subject: draft.subject, preheader: draft.preheader, body: draft.body,
        ctaLabel: draft.ctaLabel, ctaUrl: draft.ctaUrl, audience: draft.audience,
        landingSlug: draft.landingSlug,
        solutionId: draft.solutionId, businessVertical: draft.businessVertical,
        objective: draft.objective, niche: draft.niche, pain: draft.pain, offer: draft.offer, ctaType: draft.ctaType, channels: draft.channels, whatsappTemplateSid: draft.whatsappTemplateSid,
        interestWizard: { enabled: draft.interestWizard },
        ...(draft.image ? { image: draft.image } : {}),
        ...(draft.audience === 'manual_import' ? { importContacts: parseImportCsv(draft.importCsv, { consentAllRows: draft.consentAllRows }).rows } : {}),
        ...(draft.audience === 'campaign_remarketing' ? { sourceCampaignId: draft.sourceCampaignId, remarketingCohort: draft.remarketingCohort, minHoursSince: draft.minHoursSince } : {}),
        ...(draft.audience === 'clients_segment' ? { clientProductLine: draft.clientProductLine } : {}),
        ...(draft.htmlBody.trim() ? { htmlBody: draft.htmlBody } : {}),
      };
      const result = await createMarketingCampaign(payload);
      setNotice('Campaña creada en borrador. Revísala y lánzala cuando esté lista.');
      setCreating(null);
      if (result?.id) select(result.id);
    } catch (e) { setError(e.message || 'No se pudo crear la campaña.'); }
    finally { setSaving(false); }
  };

  const submitUpdate = async () => {
    if (!selected) return;
    if (!draft.name.trim()) { setError('Escribe un nombre para la campaña.'); return; }
    setSaving(true); setError('');
    try {
      await updateMarketingCampaign(selected.id, {
        name: draft.name, subject: draft.subject, preheader: draft.preheader, body: draft.body,
        ctaLabel: draft.ctaLabel, ctaUrl: draft.ctaUrl, interestWizard: { enabled: draft.interestWizard },
        landingSlug: draft.landingSlug,
        solutionId: draft.solutionId, businessVertical: draft.businessVertical,
        objective: draft.objective, niche: draft.niche, pain: draft.pain, offer: draft.offer, ctaType: draft.ctaType, channels: draft.channels, whatsappTemplateSid: draft.whatsappTemplateSid,
        ...(draft.image ? { image: draft.image } : { image: null }),
        ...(draft.htmlBody.trim() ? { htmlBody: draft.htmlBody } : { htmlBody: '' }),
      });
      setNotice('Cambios guardados. La campaña sigue en borrador hasta que la lances.');
      setCreating(null);
    } catch (e) { setError(e.message || 'No se pudieron guardar los cambios.'); }
    finally { setSaving(false); }
  };

  const configureWhatsAppTemplate = async (templateSid) => {
    if (!selected) return;
    const current = campaignToDraft(selected);
    setSaving(true); setError('');
    try {
      await updateMarketingCampaign(selected.id, {
        name: current.name, subject: current.subject, preheader: current.preheader, body: current.body,
        ctaLabel: current.ctaLabel, ctaUrl: current.ctaUrl, interestWizard: { enabled: current.interestWizard }, landingSlug: current.landingSlug,
        solutionId: current.solutionId, businessVertical: current.businessVertical, objective: current.objective, niche: current.niche,
        pain: current.pain, offer: current.offer, ctaType: current.ctaType, channels: current.channels, whatsappTemplateSid: templateSid,
        image: current.image, htmlBody: current.htmlBody, automationPlan: current.automationPlan,
      });
      setNotice('Plantilla de WhatsApp asignada a la campaña.');
      return true;
    } catch (e) { setError(e.message || 'No se pudo asignar la plantilla de WhatsApp.'); return false; }
    finally { setSaving(false); }
  };

  const submitCreateCold = async () => {
    if (!coldDraft.name.trim() || !coldDraft.subject.trim() || !coldDraft.body.trim()) { setError('Nombre, asunto y contenido son obligatorios.'); return; }
    setSaving(true); setError('');
    try {
      const { newLeadDays, inactiveDays, minDaysBetweenCampaigns, ...rest } = coldDraft;
      const result = await prepareColdLeadCampaign({ ...rest, criteria: { newLeadDays, inactiveDays, minDaysBetweenCampaigns } });
      setNotice('Campaña fría preparada con el cuestionario de calificación activado.');
      setCreating(null);
      if (result?.campaignId) select(result.campaignId);
    } catch (e) { setError(e.message || 'No se pudo preparar la campaña fría.'); }
    finally { setSaving(false); }
  };

  const doLaunch = async () => {
    if (!selected) return;
    setSaving(true); setError('');
    try { await launchMarketingCampaign(selected.id); setNotice('Campaña lista para enviar. Usa "Procesar" para mandarla.'); }
    catch (e) { setError(e.message || 'No se pudo lanzar la campaña.'); }
    finally { setSaving(false); }
  };

  const doProcess = async (dryRun) => {
    if (!selected) return;
    setProcessing(true); setError('');
    try {
      const result = await processMarketingCampaign(selected.id, { dryRun, limit: 25 });
      setNotice(dryRun
        ? `Vista previa: ${result.eligible} elegibles de ${result.requested} en cola.`
        : `Enviados: ${result.sent} · Suprimidos: ${result.suppressed} · Fallidos: ${result.failed}.`);
    } catch (e) { setError(e.message || 'No se pudo procesar la cola.'); }
    finally { setProcessing(false); }
  };

  const openAddContacts = () => { setAddContactsOpen(true); setAddContactsDraft({ importCsv: '', importFileName: '', consentAllRows: false }); setAddContactsPreview(null); setNotice(''); setError(''); };

  const handleAddContactsFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setAddContactsDraft((c) => ({ ...c, importCsv: String(reader.result || ''), importFileName: file.name }));
      setAddContactsPreview(null);
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const runAddContactsPreview = async () => {
    if (!addContactsDraft.consentAllRows) { setError('Confirma el consentimiento de la lista antes de validar.'); return; }
    const { rows, duplicatesRemoved, totalRaw, truncated } = parseImportCsv(addContactsDraft.importCsv, { consentAllRows: addContactsDraft.consentAllRows });
    if (!rows.length) { setError('Pega o sube al menos un email.'); return; }
    setError('');
    try {
      const data = await previewMarketingImport(rows);
      setAddContactsPreview({ ...data, duplicatesRemoved, totalRaw, truncated });
    } catch (e) { setError(e.message || 'No se pudo validar el CSV.'); }
  };

  const submitAddContacts = async () => {
    if (!selected || !addContactsDraft.consentAllRows) { setError('Confirma el consentimiento de la lista.'); return; }
    setAddingContacts(true); setError('');
    try {
      const { rows } = parseImportCsv(addContactsDraft.importCsv, { consentAllRows: addContactsDraft.consentAllRows });
      const result = await addContactsToCampaign({ campaignId: selected.id, importContacts: rows });
      setNotice(`${result.added} contactos agregados a la campaña. Usa "Procesar" para enviarles.`);
      setAddContactsOpen(false);
    } catch (e) { setError(e.message || 'No se pudieron agregar los contactos.'); }
    finally { setAddingContacts(false); }
  };

  return <main className={`${ADMIN_PAGE_WIDTH} mx-auto px-4 py-6 sm:px-6`}>
    <PageHeader
      breadcrumb={[{ label: 'Mercadeo' }]}
      title="Campañas"
      description="Centro de Campañas: define estrategia, audiencia, contenido y canales. Email sigue funcionando como motor de envío mientras se conectan los demás canales."
      actions={<button type="button" onClick={startCreate} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white"><PlusIcon className="h-4 w-4" />Nueva campaña</button>}
    />

    {error && <p role="alert" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
    {notice && <p className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</p>}

    <section className="mt-6">
      <SectionCard title="Campañas" subtitle="Cada campaña define primero solución, público, objetivo y canales. Desde ella se abren los estudios que ya usas, con el contexto correcto." bodyClassName="p-2">
        {loading ? <p className="p-4 text-xs text-slate-400">Cargando…</p>
          : !campaigns.length ? <EmptyState label="No hay campañas todavía. Crea una con 'Nueva campaña'." />
          : <div className="grid gap-2">{campaigns.map((c) => (
            <button key={c.id} type="button" onClick={() => select(c.id)} className="grid w-full gap-3 rounded-2xl border border-slate-100 bg-white px-4 py-3 text-left text-sm transition hover:border-emerald-200 hover:bg-emerald-50/40 lg:grid-cols-[minmax(0,2fr)_minmax(220px,1fr)_auto] lg:items-center">
              <div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><span className="truncate font-semibold text-slate-800">{c.name}</span><StatusBadge tone={CAMPAIGN_STATUS_TONE[c.status] || 'slate'} size="sm">{c.status}</StatusBadge></div><p className="mt-1 truncate text-[11px] text-slate-400">{c.campaignBrief?.niche || c.businessVertical || 'Nicho pendiente'} · {c.campaignBrief?.pain || c.subject}</p></div>
              <div className="flex flex-wrap gap-1">{(c.campaignBrief?.channels || ['email']).map((channel) => <span key={channel} className="rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold uppercase text-slate-600">{channel}</span>)}</div>
              <div className="flex items-center gap-3 lg:justify-end"><span className="text-[11px] text-slate-400">{c.enrollmentCount} cola · {c.sentCount} enviados · {c.responseCount} respuestas</span><ChevronRightIcon className="h-4 w-4 shrink-0 text-slate-300" /></div>
            </button>
          ))}</div>}
      </SectionCard>
    </section>

    {(creating || selected) && <CampaignWorkspaceModal onClose={closeWorkspace} title={creating === 'edit' ? 'Editar campaña' : creating ? 'Nueva campaña' : selected?.name || 'Campaña'}>
      <div className="min-w-0">
        {(creating === 'standard' || creating === 'edit') && <CreateForm draft={draft} setDraft={setDraft} onCancel={cancelCreate} onSubmit={creating === 'edit' ? submitUpdate : submitCreate} saving={saving}
          importPreview={importPreview} onPreviewImport={runImportPreview} onImportFile={handleImportFile} campaigns={campaigns} publishedLandings={publishedLandings}
          onDraftImageFile={handleDraftImageFile} onClearDraftImage={clearDraftImage} imageUploading={imageUploading} whatsappTemplates={whatsappTemplates}
          editing={creating === 'edit'} />}
        {creating === 'cold' && <ColdCreateForm draft={coldDraft} setDraft={setColdDraft} onCancel={cancelCreate} onSubmit={submitCreateCold} saving={saving}
          preview={coldPreview} onPreview={runColdPreview} />}
        {!creating && !selected && <SectionCard><div className="flex h-64 flex-col items-center justify-center text-center text-slate-400"><RocketLaunchIcon className="mb-3 h-10 w-10 text-slate-200" /><p className="text-sm font-medium">Selecciona una campaña o crea una nueva</p></div></SectionCard>}
        {!creating && selected && <CampaignDetail campaign={selected} onLaunch={doLaunch} onProcess={doProcess} saving={saving} processing={processing}
          onEdit={() => startEdit(selected)}
          enrollments={enrollments} enrollmentsLoading={enrollmentsLoading}
          audienceMembers={audienceMembers} audienceEligibilitySummary={audienceEligibilitySummary} audienceLoading={audienceLoading}
          addContactsOpen={addContactsOpen} onOpenAddContacts={openAddContacts} onCloseAddContacts={() => setAddContactsOpen(false)}
          addContactsDraft={addContactsDraft} setAddContactsDraft={setAddContactsDraft} onAddContactsFile={handleAddContactsFile}
          addContactsPreview={addContactsPreview} onPreviewAddContacts={runAddContactsPreview} onSubmitAddContacts={submitAddContacts} addingContacts={addingContacts}
          whatsappTemplates={whatsappTemplates} onConfigureWhatsApp={configureWhatsAppTemplate} />}
      </div>
    </CampaignWorkspaceModal>}
  </main>;
}

function CampaignWorkspaceModal({ title, children, onClose }) {
  return <div className="fixed inset-0 z-50 bg-slate-950/45 p-3 sm:p-6" role="dialog" aria-modal="true" aria-label={title}>
    <div className="mx-auto flex h-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-slate-50 shadow-2xl">
      <div className="flex shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5 py-3">
        <div><p className="text-[11px] font-bold uppercase tracking-wider text-emerald-600">Editor de campañas</p><h2 className="truncate text-base font-bold text-slate-900">{title}</h2></div>
        <button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800" aria-label="Cerrar editor"><XMarkIcon className="h-5 w-5" /></button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">{children}</div>
    </div>
  </div>;
}

function ChannelModal({ title, children, onClose }) {
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-label={title}>
    <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl"><div className="flex items-start justify-between gap-3"><div><p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Canal de campaña</p><h3 className="mt-1 text-lg font-bold text-slate-900">{title}</h3></div><button type="button" onClick={onClose} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label="Cerrar"><XMarkIcon className="h-5 w-5" /></button></div>{children}</div>
  </div>;
}

function Field({ label, children, hint, className = '' }) {
  return <label className={cx('block text-xs font-bold uppercase tracking-wide text-slate-400', className)}>{label}
    <div className="mt-1 normal-case">{children}</div>
    {hint && <p className="mt-1 text-[11px] font-normal normal-case text-slate-400">{hint}</p>}
  </label>;
}

const inputCls = 'block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800';

function CreateForm({
  draft, setDraft, onCancel, onSubmit, saving, importPreview, onPreviewImport, onImportFile, campaigns = [],
  publishedLandings = [],
  onDraftImageFile, onClearDraftImage, imageUploading, whatsappTemplates = [], editing = false,
}) {
  const change = (field, value) => setDraft((c) => ({ ...c, [field]: value }));
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [htmlFileName, setHtmlFileName] = useState('');
  const [htmlFileError, setHtmlFileError] = useState('');
  const [htmlAssets, setHtmlAssets] = useState([]);
  const [htmlAssetsUploading, setHtmlAssetsUploading] = useState(false);
  const [htmlAssetsError, setHtmlAssetsError] = useState('');
  const [showHtmlPreview, setShowHtmlPreview] = useState(false);
  const [aiInstruction, setAiInstruction] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const isImport = draft.audience === 'manual_import';
  const isCampaignRemarketing = draft.audience === 'campaign_remarketing';
  const isClientsSegment = draft.audience === 'clients_segment';
  const blockedByConsent = !editing && isImport && !draft.consentAllRows;
  const missingFields = {
    name: !draft.name.trim(),
    subject: !draft.subject.trim(),
    body: !draft.body.trim(),
  };
  const hasMissingFields = Object.values(missingFields).some(Boolean);
  const showFieldWarnings = attemptedSave && hasMissingFields;

  if (!editing) return <CampaignPlanSetup draft={draft} change={change} onCancel={onCancel} onCreate={onSubmit} saving={saving} />;

  const focusValidation = () => {
    window.requestAnimationFrame(() => document.getElementById('campaign-form-validation')?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  const submitWithValidation = () => {
    setAttemptedSave(true);
    if (hasMissingFields || blockedByConsent || (isCampaignRemarketing && !draft.sourceCampaignId)) {
      focusValidation();
      return;
    }
    onSubmit();
  };

  const generateWithAi = async () => {
    if (!draft.solutionId) {
      setAiError('Selecciona primero la solución comercial para que Impulso conozca el producto.');
      return;
    }
    if (!aiInstruction.trim()) {
      setAiError('Describe el objetivo o dolor que debe trabajar el email.');
      return;
    }
    setAiGenerating(true); setAiError('');
    try {
      const result = await generateMarketingCampaignDraft({
        solutionId: draft.solutionId,
        audience: AUDIENCE_LABELS[draft.audience] || draft.audience,
        objective: aiInstruction,
        instruction: aiInstruction,
        landingOptions: publishedLandings,
      });
      const generated = result?.draft || {};
      setDraft((current) => ({
        ...current,
        name: generated.name || current.name,
        subject: generated.subject || current.subject,
        preheader: generated.preheader || current.preheader,
        body: generated.body || current.body,
        htmlBody: generated.htmlBody || current.htmlBody,
        ctaLabel: generated.ctaLabel || current.ctaLabel,
        landingSlug: generated.landingSlug || current.landingSlug,
        automationPlan: Array.isArray(generated.automationPlan) ? generated.automationPlan : current.automationPlan,
        interestWizard: true,
      }));
    } catch (error) {
      setAiError(error?.message || 'Impulso no pudo preparar el borrador. Intenta de nuevo.');
    } finally {
      setAiGenerating(false);
    }
  };

  const handleHtmlFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setHtmlFileError('');
    if (!/\.(html?|txt)$/i.test(file.name)) {
      setHtmlFileError('Selecciona un archivo .html, .htm o .txt; no se aceptan ZIP ni documentos de diseño.');
      return;
    }
    if (file.size > HTML_FILE_MAX_BYTES) {
      setHtmlFileError('El HTML supera 750 KB. Reduce imágenes embebidas o súbelas por separado antes de cargarlo.');
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => setHtmlFileError('No se pudo leer el archivo HTML. Intenta nuevamente.');
    reader.onload = () => {
      const htmlBody = String(reader.result || '').trim();
      if (!htmlBody) { setHtmlFileError('El archivo está vacío.'); return; }
      const plainText = htmlToPlainText(htmlBody);
      setDraft((current) => ({ ...current, htmlBody, body: current.body.trim() || plainText }));
      setHtmlFileName(file.name);
      const localImages = findLocalHtmlImageSources(htmlBody);
      if (localImages.length) {
        setHtmlFileError(`Este HTML tiene ${localImages.length} imagen${localImages.length === 1 ? '' : 'es'} local${localImages.length === 1 ? '' : 'es'} (${localImages.slice(0, 3).join(', ')}). Súbelas abajo para reemplazarlas por URLs públicas.`);
      }
    };
    reader.readAsText(file);
  };

  const handleHtmlAssetFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = '';
    if (!files.length) return;
    setHtmlAssetsError('');
    setHtmlAssetsUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const asset = await uploadCampaignImage(file);
        uploaded.push({ name: file.name, ...asset });
      }
      setHtmlAssets((current) => [...current, ...uploaded]);
      setDraft((current) => {
        let htmlBody = current.htmlBody || '';
        uploaded.forEach((asset) => {
          // Reemplaza src="imagen.png" o src="carpeta/imagen.png" si vino
          // desde el HTML de diseño. Si no existía, agrega la imagen al final
          // para que el equipo pueda mover el bloque donde quiera.
          const fileName = escapeRegExp(asset.name);
          const sourcePattern = new RegExp(`(src\\s*=\\s*["'])[^"']*${fileName}(["'])`, 'ig');
          if (sourcePattern.test(htmlBody)) {
            htmlBody = htmlBody.replace(sourcePattern, `$1${asset.url}$2`);
          } else {
            htmlBody = `${htmlBody}${htmlBody ? '\\n' : ''}<img src="${asset.url}" alt="" style="display:block;max-width:100%;height:auto;" />`;
          }
        });
        return { ...current, htmlBody };
      });
    } catch (error) {
      setHtmlAssetsError(error?.message || 'No se pudo subir una de las imágenes. Usa JPG, PNG o WebP de máximo 5 MB.');
    } finally {
      setHtmlAssetsUploading(false);
    }
  };
  return <SectionCard title={editing ? 'Editar campaña' : 'Nueva campaña'} subtitle={editing ? 'Puedes editar el contenido mientras está en borrador o pausada. La audiencia y los envíos no cambian.' : undefined} action={<button type="button" onClick={onCancel} className="text-slate-400"><XMarkIcon className="h-5 w-5" /></button>}>
    <div id="campaign-form-validation" role="alert" className={cx('mb-4 rounded-lg border p-3 text-sm', attemptedSave && (hasMissingFields || blockedByConsent || (isCampaignRemarketing && !draft.sourceCampaignId)) ? 'border-amber-200 bg-amber-50 text-amber-900' : 'hidden')}>
      {hasMissingFields && <p>Antes de guardar completa: {[missingFields.name && 'nombre', missingFields.subject && 'asunto', missingFields.body && 'contenido'].filter(Boolean).join(', ')}.</p>}
      {blockedByConsent && <p className={hasMissingFields ? 'mt-1' : ''}>Esta campaña usa una lista CSV: confirma el consentimiento de marketing para habilitar el guardado.</p>}
      {isCampaignRemarketing && !draft.sourceCampaignId && <p className={(hasMissingFields || blockedByConsent) ? 'mt-1' : ''}>Selecciona la campaña de origen para el reenvío.</p>}
    </div>
    <div className="mb-5 rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 to-white p-4">
      <div className="flex items-start gap-3"><div className="rounded-lg bg-violet-600 p-2 text-white"><SparklesIcon className="h-5 w-5" /></div><div><h3 className="text-sm font-bold text-slate-900">Impulso IA — campaña completa</h3><p className="mt-0.5 text-xs leading-5 text-slate-600">Describe el objetivo. Impulso prepara asunto, HTML móvil, CTA, landing compatible y el plan de seguimiento/remarketing. Nada se guarda, envía, publica ni activa hasta que ustedes lo revisen.</p></div></div>
      <div className="mt-3 grid gap-3 sm:grid-cols-[220px_1fr_auto]">
        <select value={draft.solutionId} onChange={(event) => change('solutionId', event.target.value)} className={inputCls} aria-label="Solución para Impulso">
          <option value="">Selecciona solución…</option>
          {Object.entries(SOLUTION_OPTIONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <textarea rows={3} value={aiInstruction} onChange={(event) => setAiInstruction(event.target.value)} placeholder="Ej.: Para dueños de restaurantes: explicar que un menú digital debe aumentar el ticket con combos y promociones, e invitarlos a conocer Meso 360." className={inputCls} />
        <button type="button" onClick={generateWithAi} disabled={aiGenerating} className="self-end inline-flex items-center justify-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-violet-700 disabled:opacity-50"><SparklesIcon className="h-4 w-4" />{aiGenerating ? 'Preparando…' : 'Crear borrador'}</button>
      </div>
      {aiError && <p role="alert" className="mt-2 text-xs font-semibold text-rose-600">{aiError}</p>}
      {!draft.solutionId && <p className="mt-2 text-[11px] text-violet-700">Selecciona una solución; así Impulso usa el mensaje correcto de Meso 360, Medic 360 o Master 360.</p>}
    </div>
    {!!draft.automationPlan?.length && <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <h3 className="text-sm font-bold text-amber-950">Plan de seguimiento propuesto por Impulso</h3>
      <p className="mt-1 text-xs leading-5 text-amber-800">Se guardará con la campaña para revisión. Ninguna regla se ejecuta ni envía mensajes hasta que el equipo la apruebe e implemente.</p>
      <div className="mt-3 space-y-2">{draft.automationPlan.map((rule, index) => <div key={rule.id || index} className="rounded-lg border border-amber-200 bg-white p-3 text-xs text-slate-700">
        <div className="flex flex-wrap items-center justify-between gap-2"><strong>{automationTriggerLabel(rule.trigger)}</strong><span className="rounded-full bg-amber-100 px-2 py-0.5 font-bold text-amber-800">{automationActionLabel(rule.action)} · {Number(rule.delayHours || 0) === 0 ? 'inmediato' : `${rule.delayHours} h después`}</span></div>
        {rule.rationale && <p className="mt-1 leading-5 text-slate-600">{rule.rationale}</p>}
      </div>)}</div>
    </div>}
    <div className="grid gap-4 sm:grid-cols-2">
      <Field label="Nombre"><input value={draft.name} onChange={(e) => change('name', e.target.value)} aria-invalid={showFieldWarnings && missingFields.name} className={cx(inputCls, showFieldWarnings && missingFields.name && 'border-rose-400 ring-1 ring-rose-300')} />{showFieldWarnings && missingFields.name && <p className="mt-1 text-xs font-medium normal-case text-rose-600">Escribe un nombre para identificar esta campaña.</p>}</Field>
      <Field label="Audiencia"><select value={draft.audience} onChange={(e) => change('audience', e.target.value)} className={inputCls}>
        {Object.entries(AUDIENCE_LABELS).filter(([v]) => v !== 'cold_leads').map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select></Field>
      <Field label="Solución comercial" hint="Define qué producto promueve la campaña y prepara el journey correcto.">
        <select value={draft.solutionId} onChange={(e) => change('solutionId', e.target.value)} className={inputCls}>
          <option value="">Pendiente de definir</option>
          {Object.entries(SOLUTION_OPTIONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </Field>
      <Field label="Objetivo central"><select value={draft.objective} onChange={(e) => change('objective', e.target.value)} className={inputCls}><option value="conversation">Generar conversación</option><option value="lead">Capturar lead</option><option value="traffic">Llevar tráfico</option><option value="remarketing">Remarketing</option><option value="sale">Venta</option></select></Field>
      <Field label="Nicho / ubicación" hint="Ej. Restaurantes independientes · San Juan"><input value={draft.niche} onChange={(e) => change('niche', e.target.value)} className={inputCls} /></Field>
      <Field label="Dolor principal" className="sm:col-span-2"><input value={draft.pain} onChange={(e) => change('pain', e.target.value)} placeholder="Ej. El menú no impulsa promociones ni aumenta el ticket promedio." className={inputCls} /></Field>
      <Field label="CTA principal"><select value={draft.ctaType} onChange={(e) => change('ctaType', e.target.value)} className={inputCls}><option value="wizard">Wizard de calificación</option><option value="landing">Landing</option><option value="whatsapp">WhatsApp</option><option value="messenger">Messenger</option></select></Field>
      <Field label="Canales de la campaña" hint="Elige cómo se ejecutará la campaña. WhatsApp crea una audiencia y un mensaje para aprobar; no manda mensajes libres ni salta la plantilla aprobada." className="sm:col-span-2"><div className="mt-1 flex flex-wrap gap-2">{[['email', 'Email'], ['meta', 'Meta'], ['landing', 'Landing'], ['whatsapp', 'WhatsApp'], ['sms', 'SMS'], ['messenger', 'Messenger']].map(([value, label]) => <label key={value} className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 text-xs font-semibold ${draft.channels.includes(value) ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-200 bg-white text-slate-500'}`}><input type="checkbox" checked={draft.channels.includes(value)} onChange={(e) => change('channels', e.target.checked ? [...draft.channels, value] : draft.channels.filter((channel) => channel !== value))} />{label}</label>)}</div></Field>
      {draft.channels.includes('whatsapp') && <Field label="Plantilla de WhatsApp" hint="Se consulta desde Twilio. Selecciona una plantilla cuya elegibilidad para WhatsApp ya verificaste en Twilio."><select value={draft.whatsappTemplateSid} onChange={(e) => change('whatsappTemplateSid', e.target.value)} className={inputCls}><option value="">Selecciona una plantilla…</option>{whatsappTemplates.map((template) => <option key={template.id} value={template.id}>{template.friendlyName || template.templateName} · {template.language || 'sin idioma'} · {template.contentType || 'plantilla'}</option>)}</select>{!whatsappTemplates.length && <p className="mt-1 text-[11px] text-amber-700">Twilio no devolvió plantillas. Actualiza el catálogo o revisa las credenciales de mensajería.</p>}</Field>}
      <Field label="Asunto del email" className="sm:col-span-2"><input value={draft.subject} onChange={(e) => change('subject', e.target.value)} aria-invalid={showFieldWarnings && missingFields.subject} className={cx(inputCls, showFieldWarnings && missingFields.subject && 'border-rose-400 ring-1 ring-rose-300')} />{showFieldWarnings && missingFields.subject && <p className="mt-1 text-xs font-medium normal-case text-rose-600">El asunto es obligatorio.</p>}</Field>
      <Field label="Preheader (opcional)"><input value={draft.preheader} onChange={(e) => change('preheader', e.target.value)} className={inputCls} /></Field>
      <Field label="Botón (texto)"><input value={draft.ctaLabel} onChange={(e) => change('ctaLabel', e.target.value)} className={inputCls} /></Field>
    </div>
    <div className="mt-4"><Field label="Imagen del email (opcional)" hint="Se muestra arriba del texto, a todo el ancho. Ideal: banner horizontal, menos de 1MB.">
      {draft.image?.url
        ? <div className="flex items-start gap-3">
            <img src={draft.image.url} alt="" className="h-24 w-40 rounded-lg border border-slate-200 object-cover" />
            <div className="flex flex-col gap-2">
              <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50">
                <ArrowUpTrayIcon className="h-3.5 w-3.5" />Cambiar imagen
                <input type="file" accept="image/*" onChange={onDraftImageFile} className="hidden" />
              </label>
              <button type="button" onClick={onClearDraftImage} className="text-xs font-semibold text-rose-600 hover:underline">Quitar imagen</button>
            </div>
          </div>
        : <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-3 text-xs font-bold text-slate-600 hover:bg-slate-100">
            <ArrowUpTrayIcon className="h-4 w-4" />{imageUploading ? 'Subiendo…' : 'Subir imagen'}
            <input type="file" accept="image/*" onChange={onDraftImageFile} disabled={imageUploading} className="hidden" />
          </label>}
    </Field></div>
    <div className="mt-4"><Field label="Contenido" hint="Texto plano. Es lo que se ve por defecto (imagen + texto + botón), y también el respaldo para bandejas que no muestran HTML.">
      <textarea rows={6} value={draft.body} onChange={(e) => change('body', e.target.value)} aria-invalid={showFieldWarnings && missingFields.body} className={cx(inputCls, showFieldWarnings && missingFields.body && 'border-rose-400 ring-1 ring-rose-300')} />
      {showFieldWarnings && missingFields.body && <p className="mt-1 text-xs font-medium normal-case text-rose-600">El contenido es obligatorio, incluso con HTML, para bandejas que no muestran diseño.</p>}
    </Field></div>
    <details className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <summary className="cursor-pointer text-xs font-bold uppercase tracking-wide text-slate-500">HTML personalizado (avanzado, opcional)</summary>
      <p className="mt-2 text-[11px] text-slate-400">Si lo llenas, reemplaza la imagen/texto/botón de arriba por tu propio diseño (tarjetas, botones de color, varios enlaces). Variables disponibles: <code>{'{{firstName}}'}</code>, <code>{'{{CTA_URL}}'}</code>, <code>{'{{CTA_URL_ERP}}'}</code>, <code>{'{{CTA_URL_RESTAURANTE}}'}</code>.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50">
          <ArrowUpTrayIcon className="h-3.5 w-3.5" />Subir archivo HTML
          <input type="file" accept=".html,.htm,.txt,text/html,text/plain" onChange={handleHtmlFile} className="hidden" />
        </label>
        <span className="text-[11px] text-slate-400">Máximo 750 KB{htmlFileName ? ` · ${htmlFileName} cargado` : ''}</span>
      </div>
      {htmlFileError && <p role="alert" className="mt-2 text-xs font-medium text-rose-600">{htmlFileError}</p>}
      <textarea rows={10} value={draft.htmlBody} onChange={(e) => change('htmlBody', e.target.value)} placeholder="<div>...</div>" className={cx(inputCls, 'mt-2 font-mono text-xs')} />
      <div className="mt-3 rounded-lg border border-sky-200 bg-sky-50 p-3">
        <p className="text-xs font-bold text-sky-900">Imágenes dentro del HTML</p>
        <p className="mt-1 text-[11px] leading-4 text-sky-700">Sube las imágenes que usó diseño. Si el HTML tenía una ruta local como <code>images/banner.png</code>, una imagen con ese mismo nombre la reemplaza automáticamente por una URL pública de BBTS.</p>
        <label className="mt-2 inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-sky-300 bg-white px-3 py-1.5 text-xs font-bold text-sky-800 hover:bg-sky-100">
          <ArrowUpTrayIcon className="h-3.5 w-3.5" />{htmlAssetsUploading ? 'Subiendo imágenes…' : 'Subir imágenes al HTML'}
          <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleHtmlAssetFiles} disabled={htmlAssetsUploading} className="hidden" />
        </label>
        {htmlAssetsError && <p role="alert" className="mt-2 text-xs font-medium text-rose-600">{htmlAssetsError}</p>}
        {!!htmlAssets.length && <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">{htmlAssets.map((asset) => <div key={`${asset.id}-${asset.name}`} className="overflow-hidden rounded border border-sky-200 bg-white p-1">
          <img src={asset.url} alt={asset.name} className="h-16 w-full object-cover" />
          <p className="truncate px-1 pt-1 text-[10px] text-slate-500" title={asset.name}>{asset.name}</p>
        </div>)}</div>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => setShowHtmlPreview((current) => !current)} disabled={!draft.htmlBody.trim()} className="rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-xs font-bold text-violet-700 hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-40">
          {showHtmlPreview ? 'Ocultar vista previa' : 'Ver vista previa del email'}
        </button>
        {!draft.htmlBody.trim() && <span className="text-[11px] text-slate-400">Carga o pega el HTML para habilitar la vista previa.</span>}
      </div>
      {showHtmlPreview && draft.htmlBody.trim() && <div className="mt-3 overflow-hidden rounded-lg border border-slate-300 bg-slate-200 p-3">
        <div className="mb-2 flex items-center justify-between text-[11px] font-semibold text-slate-500"><span>Vista previa del contenido HTML</span><span>Escritorio</span></div>
        <iframe title="Vista previa de campaña" sandbox="" srcDoc={draft.htmlBody} className="h-[580px] w-full rounded bg-white" />
      </div>}
    </details>
    <div className="mt-4 grid gap-4 sm:grid-cols-2">
      <Field label="Landing conectada" hint="Selecciona una landing publicada. BBTS insertará automáticamente un enlace único por contacto y medirá apertura → landing → wizard → lead.">
        <select value={draft.landingSlug || ''} onChange={(e) => change('landingSlug', e.target.value)} className={inputCls}>
          <option value="">No usar landing en esta campaña</option>
          {publishedLandings.map((landing) => <option key={landing.slug} value={landing.slug}>{landing.title} · /soluciones/{landing.slug}</option>)}
        </select>
        {!publishedLandings.length && <p className="mt-1 text-[11px] font-normal normal-case text-amber-700">No hay landings publicadas aún. Publícala en Landing Studio y aparecerá aquí.</p>}
      </Field>
      <Field label="URL del botón (opcional, https)" hint="Si activas el cuestionario abajo, este botón se reemplaza automáticamente por el enlace del cuestionario.">
        <input value={draft.ctaUrl} onChange={(e) => change('ctaUrl', e.target.value)} placeholder={draft.landingSlug ? 'Automática: la landing seleccionada' : 'https://'} disabled={Boolean(draft.landingSlug)} className={cx(inputCls, draft.landingSlug && 'cursor-not-allowed bg-slate-100 text-slate-400')} />
      </Field>
      <label className="flex items-center gap-2 self-end rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 text-sm font-semibold text-violet-800">
        <input type="checkbox" checked={draft.interestWizard} onChange={(e) => change('interestWizard', e.target.checked)} />
        Activar cuestionario de calificación en el email
      </label>
    </div>
    {isClientsSegment && <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3">
      <Field label="Línea de producto" hint="Se toma directo de la etiqueta que le pusiste a cada cliente en Clientes — no hace falta subir CSV. Ya tienen consentimiento por ser clientes activos; se respeta cualquiera que haya pedido no recibir más correos.">
        <select value={draft.clientProductLine} onChange={(e) => change('clientProductLine', e.target.value)} className={inputCls}>
          {Object.entries(CLIENT_PRODUCT_LINE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </Field>
    </div>}
    {isCampaignRemarketing && <div className="mt-4 rounded-lg border border-violet-200 bg-violet-50 p-3">
      <Field label="Campaña de origen" hint="De qué campaña se reenvía a quien no completó el paso siguiente.">
        <select value={draft.sourceCampaignId} onChange={(e) => change('sourceCampaignId', e.target.value)} className={inputCls}>
          <option value="">Selecciona una campaña…</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.sentCount} enviados)</option>)}
        </select>
      </Field>
      <div className="mt-3 space-y-2">
        {Object.entries(REMARKETING_COHORT_LABELS).map(([value, label]) => (
          <label key={value} className="flex items-center gap-2 rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm font-semibold text-violet-900">
            <input type="radio" name="remarketingCohort" checked={draft.remarketingCohort === value} onChange={() => change('remarketingCohort', value)} />
            {label}
          </label>
        ))}
      </div>
      <Field label="Esperar al menos (horas) antes de reenviar" className="mt-3">
        <input type="number" min="1" max="720" value={draft.minHoursSince} onChange={(e) => change('minHoursSince', Number(e.target.value))} className={cx(inputCls, 'w-32')} />
      </Field>
    </div>}
    {editing && isImport && <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-xs text-sky-800">Esta campaña ya tiene una lista importada. Aquí editas el diseño y el mensaje; los contactos existentes permanecen intactos.</div>}
    {isImport && !editing && <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">
          <ArrowUpTrayIcon className="h-4 w-4" />Subir CSV
          <input type="file" accept=".csv,text/csv,text/plain" onChange={onImportFile} className="hidden" />
        </label>
        {draft.importFileName && <span className="text-xs text-slate-500">{draft.importFileName}</span>}
      </div>
      <Field label="Contactos" className="mt-3" hint="Acepta solo emails (uno por línea, con o sin encabezado) o CSV completo: email,nombre,empresa,teléfono,línea(erp/restaurante),consentimiento(si/no). Los duplicados se eliminan solos.">
        <textarea rows={5} value={draft.importCsv} onChange={(e) => change('importCsv', e.target.value)} placeholder={'juan@empresa.com\nmaria@negocio.com'} className={cx(inputCls, 'font-mono')} />
      </Field>
      <label className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs font-semibold text-amber-900">
        <input type="checkbox" className="mt-0.5" checked={draft.consentAllRows} onChange={(e) => change('consentAllRows', e.target.checked)} />
        Confirmo que esta lista tiene consentimiento de marketing por email (ej. se registraron en Mautic o el landing). Si el CSV trae una columna de consentimiento por fila, se respeta esa; si no, aplica a toda la lista.
      </label>
      <button type="button" onClick={onPreviewImport} disabled={blockedByConsent} className="mt-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 disabled:opacity-40">Validar lista</button>
      {importPreview && <p className="mt-2 text-xs text-slate-500">
        {importPreview.totalRaw} filas leídas{importPreview.duplicatesRemoved ? ` · ${importPreview.duplicatesRemoved} duplicados eliminados` : ''}{importPreview.truncated ? ` · se recortó a las primeras ${IMPORT_ROW_LIMIT} (límite por campaña)` : ''} · {Object.entries(importPreview.counts || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')}
      </p>}
    </div>}
    <div className="mt-5 flex justify-end gap-2">
      <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
      <button type="button" onClick={submitWithValidation} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><CheckIcon className="h-4 w-4" />{saving ? 'Guardando…' : editing ? 'Guardar cambios' : 'Crear campaña'}</button>
    </div>
  </SectionCard>;
}

function CampaignPlanSetup({ draft, change, onCancel, onCreate, saving }) {
  const channels = [
    ['email', 'Email', 'Secuencia y seguimiento'],
    ['landing', 'Landing', 'Experiencia y wizard web'],
    ['meta', 'Meta Ads', 'Contenido, video y pauta'],
    ['whatsapp', 'WhatsApp', 'Plantilla aprobada'],
    ['messenger', 'Messenger', 'Conversación desde Meta'],
    ['sms', 'SMS', 'Mensaje directo'],
  ];
  const ready = Boolean(draft.name.trim() && draft.solutionId && draft.objective && draft.audience && draft.channels.length);
  const toggleChannel = (channel) => change('channels', draft.channels.includes(channel)
    ? draft.channels.filter((item) => item !== channel)
    : [...draft.channels, channel]);
  return <SectionCard title="Plan de la campaña" subtitle="Primero decide qué vas a promover, para quién y por cuáles canales. Después solo verás las herramientas necesarias para preparar esa campaña." action={<button type="button" onClick={onCancel} className="text-slate-400"><XMarkIcon className="h-5 w-5" /></button>}>
    <div className="mb-6 flex items-center gap-2 text-xs font-bold"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white">1</span><span className="text-emerald-700">Plan</span><span className="h-px w-10 bg-slate-200" /><span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-400">2</span><span className="text-slate-400">Preparar activos</span><span className="h-px w-10 bg-slate-200" /><span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-400">3</span><span className="text-slate-400">Revisar y activar</span></div>
    <div className="grid gap-5 md:grid-cols-2">
      <Field label="Nombre de la campaña" hint="Solo para identificarla dentro de BBTS."><input value={draft.name} onChange={(event) => change('name', event.target.value)} placeholder="Ej.: Meso 360 · restaurantes PR · octubre" className={inputCls} /></Field>
      <Field label="1. Solución que vas a promover" hint="Esto define el mensaje, wizard y estudios que se abrirán después."><select value={draft.solutionId} onChange={(event) => change('solutionId', event.target.value)} className={inputCls}><option value="">Selecciona una solución…</option>{Object.entries(SOLUTION_OPTIONS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
      <Field label="2. Resultado que buscas"><select value={draft.objective} onChange={(event) => change('objective', event.target.value)} className={inputCls}><option value="conversation">Generar conversaciones</option><option value="lead">Capturar leads</option><option value="traffic">Llevar tráfico</option><option value="remarketing">Remarketing</option><option value="sale">Venta</option></select></Field>
      <Field label="3. Audiencia"><select value={draft.audience} onChange={(event) => change('audience', event.target.value)} className={inputCls}>{Object.entries(AUDIENCE_LABELS).filter(([value]) => value !== 'cold_leads').map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></Field>
      <Field label="4. Nicho o ubicación" hint="Ej.: restaurantes independientes · San Juan"><input value={draft.niche} onChange={(event) => change('niche', event.target.value)} className={inputCls} /></Field>
    </div>
    <Field label="5. Canales que formarán parte de esta campaña" hint="No se crean herramientas nuevas. Al continuar, aparecerán solo los estudios y configuraciones correspondientes."><div className="mt-2 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">{channels.map(([value, label, description]) => <button key={value} type="button" onClick={() => toggleChannel(value)} className={cx('rounded-xl border p-3 text-left transition', draft.channels.includes(value) ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-200' : 'border-slate-200 bg-white hover:border-slate-300')}><span className="flex items-center justify-between gap-2"><strong className="text-sm text-slate-800">{label}</strong><span className={cx('h-4 w-4 rounded-full border', draft.channels.includes(value) ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300 bg-white')} /></span><span className="mt-1 block text-xs text-slate-500">{description}</span></button>)}</div></Field>
    <div className="mt-6 flex flex-wrap justify-end gap-2"><button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button><button type="button" onClick={onCreate} disabled={!ready || saving} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{saving ? 'Creando…' : 'Crear campaña'} <ChevronRightIcon className="h-4 w-4" /></button></div>
  </SectionCard>;
}

function ColdCreateForm({ draft, setDraft, onCancel, onSubmit, saving, preview, onPreview }) {
  const change = (field, value) => setDraft((c) => ({ ...c, [field]: value }));
  return <SectionCard title="Campaña fría con cuestionario" subtitle="Leads nuevos sin contactar o inactivos por falta de seguimiento. El cuestionario de calificación se activa automáticamente." action={<button type="button" onClick={onCancel} className="text-slate-400"><XMarkIcon className="h-5 w-5" /></button>}>
    <div className="grid gap-4 sm:grid-cols-3">
      <Field label="Leads nuevos sin contactar (días)"><input type="number" min="1" max="90" value={draft.newLeadDays} onChange={(e) => change('newLeadDays', Number(e.target.value))} className={inputCls} /></Field>
      <Field label="Sin actividad (días)"><input type="number" min="1" max="365" value={draft.inactiveDays} onChange={(e) => change('inactiveDays', Number(e.target.value))} className={inputCls} /></Field>
      <Field label="Días mínimos entre campañas" hint="No reenviar a quien ya recibió una campaña recientemente."><input type="number" min="7" max="365" value={draft.minDaysBetweenCampaigns} onChange={(e) => change('minDaysBetweenCampaigns', Number(e.target.value))} className={inputCls} /></Field>
    </div>
    <div className="mt-3"><button type="button" onClick={onPreview} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700">Calcular audiencia</button>
      {preview && <p className="mt-2 text-xs text-slate-500">{preview.eligibleCount} leads elegibles. Excluidos: {Object.entries(preview.excluded || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || 'ninguno'}.</p>}
    </div>
    <div className="mt-4 grid gap-4 sm:grid-cols-2">
      <Field label="Nombre de la campaña"><input value={draft.name} onChange={(e) => change('name', e.target.value)} className={inputCls} /></Field>
      <Field label="Asunto del email"><input value={draft.subject} onChange={(e) => change('subject', e.target.value)} className={inputCls} /></Field>
    </div>
    <div className="mt-4"><Field label="Contenido"><textarea rows={4} value={draft.body} onChange={(e) => change('body', e.target.value)} className={inputCls} /></Field></div>
    <div className="mt-5 flex justify-end gap-2">
      <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
      <button type="button" onClick={onSubmit} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><SparklesIcon className="h-4 w-4" />{saving ? 'Guardando…' : 'Preparar campaña fría'}</button>
    </div>
  </SectionCard>;
}

function CampaignDetail({
  campaign, onLaunch, onProcess, saving, processing, enrollments, enrollmentsLoading,
  audienceMembers, audienceEligibilitySummary = {}, audienceLoading,
  addContactsOpen, onOpenAddContacts, onCloseAddContacts, addContactsDraft, setAddContactsDraft,
  onAddContactsFile, addContactsPreview, onPreviewAddContacts, onSubmitAddContacts, addingContacts, onEdit, whatsappTemplates = [], onConfigureWhatsApp,
}) {
  const campaignChannels = Array.isArray(campaign.campaignBrief?.channels) && campaign.campaignBrief.channels.length ? campaign.campaignBrief.channels : ['email'];
  const usesEmail = campaignChannels.includes('email');
  const canLaunch = usesEmail && ['draft', 'paused'].includes(campaign.status);
  const canEdit = ['draft', 'paused'].includes(campaign.status);
  const canProcess = ['ready', 'running'].includes(campaign.status);
  const canAddContacts = !['completed', 'archived'].includes(campaign.status);
  const [answersModal, setAnswersModal] = useState(null); // { enrollment, response, loading, error }
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(false);
  const [contactsModal, setContactsModal] = useState(null); // { label, loading, error, rows }
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(''); // mensaje corto de éxito/error
  const [whatsappModal, setWhatsappModal] = useState(false);
  const [whatsappTemplateSid, setWhatsappTemplateSid] = useState(campaign.whatsappTemplate?.id || '');

  const sendTest = async () => {
    setTestSending(true); setTestResult('');
    try {
      const result = await sendMarketingCampaignTest({ campaignId: campaign.id, testEmail: testEmail.trim() || undefined });
      setTestResult(`✅ Enviado a ${result.sentTo}`);
    } catch (e) {
      setTestResult(`❌ ${e.message || 'No se pudo enviar la prueba.'}`);
    } finally {
      setTestSending(false);
    }
  };

  const openContactsForStatus = async (status, label) => {
    setContactsModal({ label, loading: true, error: '', rows: [] });
    try {
      const q = query(
        collection(db, 'marketing_campaigns', campaign.id, 'enrollments'),
        where('status', '==', status),
        orderBy('updatedAt', 'desc'),
        fsLimit(500)
      );
      const snap = await getDocs(q);
      setContactsModal({
        label,
        loading: false,
        error: '',
        rows: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
      });
    } catch (err) {
      setContactsModal({ label, loading: false, error: err.message || 'No se pudo cargar la lista.', rows: [] });
    }
  };
  const closeContactsModal = () => setContactsModal(null);

  // Conteo en vivo por estado del enrollment — así se ve exactamente dónde
  // está cada contacto (no solo cuántos se enviaron), sin bajar los 200
  // enrollments que ya carga la tabla de Resultados.
  const loadFunnel = useCallback(async () => {
    setFunnelLoading(true);
    try {
      const base = collection(db, 'marketing_campaigns', campaign.id, 'enrollments');
      const statuses = ['queued', 'processing', 'sent', 'delivered', 'opened', 'clicked', 'responded', 'suppressed', 'failed', 'converted'];
      const counts = await Promise.all(statuses.map((s) => getCountFromServer(query(base, where('status', '==', s)))));
      const byStatus = {};
      statuses.forEach((s, i) => { byStatus[s] = counts[i].data().count; });
      setFunnel(byStatus);
    } catch (err) {
      setFunnel({ error: err.message || 'No se pudo calcular el embudo.' });
    } finally {
      setFunnelLoading(false);
    }
  }, [campaign.id]);

  useEffect(() => { loadFunnel(); }, [loadFunnel]);

  const openAnswers = async (en) => {
    setAnswersModal({ enrollment: en, response: null, loading: true, error: '' });
    try {
      const snap = await getDoc(doc(db, 'marketing_campaigns', campaign.id, 'interest_responses', en.id));
      setAnswersModal({ enrollment: en, response: snap.exists() ? snap.data() : null, loading: false, error: snap.exists() ? '' : 'No se encontraron respuestas guardadas para este contacto.' });
    } catch (err) {
      setAnswersModal({ enrollment: en, response: null, loading: false, error: err.message || 'No se pudieron cargar las respuestas.' });
    }
  };
  const closeAnswers = () => setAnswersModal(null);

  return <div className="space-y-5">
    <SectionCard title={campaign.name} subtitle={`Asunto: ${campaign.subject}`} action={<StatusBadge tone={CAMPAIGN_STATUS_TONE[campaign.status] || 'slate'}>{campaign.status}</StatusBadge>}>
      {campaign.image?.url && <img src={campaign.image.url} alt="" className="mb-4 h-32 w-full max-w-md rounded-lg border border-slate-200 object-cover" />}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label="En cola" value={campaign.enrollmentCount} />
        <Stat label="Enviados" value={campaign.sentCount} />
        <Stat label="Respuestas" value={campaign.responseCount} />
        <Stat label="Audiencia" value={campaign.audienceCount} />
      </div>
      <div className="mt-5 flex flex-wrap gap-2">
        {canEdit && <button type="button" onClick={onEdit} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-bold text-violet-700 disabled:opacity-50"><SparklesIcon className="h-4 w-4" />Editar plan</button>}
        {canAddContacts && <button type="button" onClick={onOpenAddContacts} className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-sky-300 bg-sky-50 px-4 py-2 text-sm font-bold text-sky-700"><ArrowUpTrayIcon className="h-4 w-4" />Agregar lista (CSV)</button>}
        {canLaunch && <button type="button" onClick={onLaunch} disabled={saving} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><RocketLaunchIcon className="h-4 w-4" />{campaign.status === 'paused' ? 'Reanudar campaña' : 'Lanzar (encolar audiencia)'}</button>}
        {canProcess && <>
          <button type="button" onClick={() => onProcess(true)} disabled={processing} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"><ArrowPathIcon className="h-4 w-4" />Vista previa (dry run)</button>
          <button type="button" onClick={() => onProcess(false)} disabled={processing} className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><PaperAirplaneIcon className="h-4 w-4" />{processing ? 'Enviando…' : 'Enviar 25 de la cola'}</button>
        </>}
      </div>
      {usesEmail && <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
        <span className="text-xs font-bold text-amber-800">Enviar prueba</span>
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="tu@email.com (vacío = tu correo)"
          className="min-w-0 flex-1 rounded-lg border border-amber-200 bg-white px-3 py-1.5 text-sm"
        />
        <button type="button" onClick={sendTest} disabled={testSending} className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">
          {testSending ? 'Enviando…' : 'Enviar de prueba'}
        </button>
        {testResult && <span className="text-xs font-semibold text-amber-900">{testResult}</span>}
      </div>}
    </SectionCard>

    <CampaignOrchestra campaign={campaign} onConfigureWhatsApp={() => { setWhatsappTemplateSid(campaign.whatsappTemplate?.id || ''); setWhatsappModal(true); }} />

    {whatsappModal && <ChannelModal title="Configurar WhatsApp" onClose={() => setWhatsappModal(false)}>
      <p className="text-sm leading-6 text-slate-600">Selecciona la plantilla aprobada de Twilio que esta campaña puede usar. No se envía ningún mensaje aquí.</p>
      <label className="mt-4 block text-xs font-bold uppercase tracking-wide text-slate-500">Plantilla aprobada<select value={whatsappTemplateSid} onChange={(event) => setWhatsappTemplateSid(event.target.value)} className="mt-1 block w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800"><option value="">Selecciona una plantilla…</option>{whatsappTemplates.map((template) => <option key={template.id} value={template.id}>{template.friendlyName || template.templateName} · {template.language || 'sin idioma'} · {template.contentType || 'plantilla'}</option>)}</select></label>
      {!whatsappTemplates.length && <p className="mt-2 text-sm text-amber-700">No llegaron plantillas desde Twilio. Actualiza el catálogo de mensajería.</p>}
      <div className="mt-5 flex justify-end gap-2"><button type="button" onClick={() => setWhatsappModal(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button><button type="button" disabled={!whatsappTemplateSid || saving} onClick={async () => { if (await onConfigureWhatsApp(whatsappTemplateSid)) setWhatsappModal(false); }} className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{saving ? 'Guardando…' : 'Asignar plantilla'}</button></div>
    </ChannelModal>}

    <SectionCard title="Audiencia central" subtitle="Empresas y contactos añadidos desde Prospección. Estar aquí no les envía nada; el canal se activa después con su autorización.">
      {audienceLoading ? <p className="text-xs text-slate-400">Cargando audiencia…</p>
        : !audienceMembers.length ? <p className="text-sm text-slate-500">Todavía no hay prospectos añadidos. Ve a Prospección, selecciona esta campaña y usa “Añadir a audiencia”.</p>
        : <><AudienceEligibilitySummary summary={audienceEligibilitySummary} /><div className="mt-3 divide-y divide-slate-100">{audienceMembers.map((member) => <div key={member.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5 text-sm">
          <div><p className="font-semibold text-slate-800">{member.candidateName || member.contactName || 'Contacto'}</p><p className="text-xs text-slate-400">{member.email || member.phone || 'Sin dato de contacto'}</p></div>
          <div className="flex flex-wrap gap-1">{Object.entries(member.channelEligibility || {}).map(([channel, value]) => { const state = typeof value === 'string' ? value : value?.state; const reason = typeof value === 'string' ? '' : value?.reason; return <span key={channel} title={reason} className={`rounded-full px-2 py-1 text-[10px] font-bold uppercase ${['eligible', 'template_required'].includes(state) ? 'bg-emerald-50 text-emerald-700' : state === 'suppressed' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>{channel}: {state}</span>; })}</div>
        </div>)}</div></>}
    </SectionCard>

    <SectionCard title="Embudo" subtitle="En qué paso está cada contacto ahora mismo — abrió, hizo clic, o completó el cuestionario."
      action={<button type="button" onClick={loadFunnel} disabled={funnelLoading} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:opacity-50"><ArrowPathIcon className="h-3.5 w-3.5" />{funnelLoading ? 'Calculando…' : 'Actualizar'}</button>}>
      {!funnel ? <p className="text-xs text-slate-400">Calculando…</p>
        : funnel.error ? <p className="text-sm text-rose-600">{funnel.error}</p>
        : <FunnelBreakdown funnel={funnel} audienceCount={campaign.audienceCount} onViewContacts={openContactsForStatus} />}
    </SectionCard>

    {contactsModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h3 className="text-lg font-semibold text-gray-800">{contactsModal.label} ({contactsModal.rows.length})</h3>
          <button onClick={closeContactsModal} className="text-gray-500 hover:text-red-600">✕</button>
        </div>
        <div className="px-6 py-4">
          {contactsModal.loading && <p className="text-sm text-slate-400">Cargando…</p>}
          {!contactsModal.loading && contactsModal.error && <p className="text-sm text-rose-600">{contactsModal.error}</p>}
          {!contactsModal.loading && !contactsModal.error && !contactsModal.rows.length && <p className="text-sm text-slate-400">Nadie en este grupo ahora mismo.</p>}
          {!contactsModal.loading && contactsModal.rows.length > 0 && (
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-100 text-left text-xs font-bold uppercase text-slate-400">
                <th className="py-2 pr-3">Contacto</th><th className="py-2 pr-3">Última actividad</th>
              </tr></thead>
              <tbody>{contactsModal.rows.map((row) => (
                <tr key={row.id} className="border-b border-slate-50">
                  <td className="py-2 pr-3"><p className="font-semibold text-slate-800">{row.contactName || '—'}</p><p className="text-xs text-slate-400">{row.email}</p></td>
                  <td className="py-2 pr-3 text-xs text-slate-500">{row.updatedAt?.toDate ? row.updatedAt.toDate().toLocaleString('es-PR') : '—'}</td>
                </tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </div>
    </div>}

    {addContactsOpen && <AddContactsPanel draft={addContactsDraft} setDraft={setAddContactsDraft} onCancel={onCloseAddContacts}
      onFile={onAddContactsFile} preview={addContactsPreview} onPreview={onPreviewAddContacts} onSubmit={onSubmitAddContacts} saving={addingContacts} />}

    <SectionCard title="Resultados" subtitle="Quién respondió el cuestionario de calificación y con qué score.">
      {enrollmentsLoading ? <p className="text-xs text-slate-400">Cargando…</p>
        : !enrollments.length ? <EmptyState label="Todavía no hay enrollments para esta campaña." />
        : <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="border-b border-slate-100 text-left text-xs font-bold uppercase text-slate-400">
            <th className="py-2 pr-3">Contacto</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Score</th><th className="py-2 pr-3">Prioridad</th><th className="py-2 pr-3"></th>
          </tr></thead><tbody>{enrollments.map((en) => (
            <tr key={en.id} className="border-b border-slate-50">
              <td className="py-2 pr-3"><p className="font-semibold text-slate-800">{en.contactName || en.email}</p><p className="text-xs text-slate-400">{en.email}</p></td>
              <td className="py-2 pr-3"><StatusBadge tone={ENROLLMENT_STATUS_TONE[en.status] || 'slate'} size="sm">{en.status}</StatusBadge></td>
              <td className="py-2 pr-3">{typeof en.qualificationScore === 'number' ? en.qualificationScore : '—'}</td>
              <td className="py-2 pr-3">{en.priority || '—'}</td>
              <td className="py-2 pr-3">{en.status === 'responded' && <button type="button" onClick={() => openAnswers(en)} className="text-xs font-semibold text-sky-600 hover:text-sky-800">Ver respuestas</button>}</td>
            </tr>
          ))}</tbody></table></div>}
    </SectionCard>

    {answersModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-lg">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Respuestas de {answersModal.enrollment.contactName || answersModal.enrollment.email}</h3>
            <p className="text-xs text-gray-500">
              {typeof answersModal.enrollment.qualificationScore === 'number' ? `Score ${answersModal.enrollment.qualificationScore} (${answersModal.enrollment.priority || '—'})` : 'Sin score'}
            </p>
          </div>
          <button onClick={closeAnswers} className="text-gray-500 hover:text-red-600">✕</button>
        </div>
        <div className="space-y-3 px-6 py-5">
          {answersModal.loading && <p className="text-sm text-slate-400">Cargando…</p>}
          {!answersModal.loading && answersModal.error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-600">{answersModal.error}</p>}
          {!answersModal.loading && answersModal.response && (
            <dl className="space-y-2">
              {Object.entries(answersModal.response.answers || {}).map(([key, value]) => (
                <div key={key} className="flex justify-between gap-4 border-b border-slate-50 pb-2 text-sm">
                  <dt className="font-medium text-slate-500">{key}</dt>
                  <dd className="text-right text-slate-800">{Array.isArray(value) ? value.join(', ') : String(value)}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>
    </div>}
  </div>;
}

function CampaignOrchestra({ campaign, onConfigureWhatsApp }) {
  const solution = campaign.solutionId || 'general';
  const query = new URLSearchParams({ campaignId: campaign.id, solution }).toString();
  const channels = Array.isArray(campaign.campaignBrief?.channels) && campaign.campaignBrief.channels.length ? campaign.campaignBrief.channels : ['email'];
  const channelStates = campaign.channelStates || {};
  const stateLabel = (channel) => ({ not_configured: 'Pendiente', ready: 'Listo', active: 'Activo', paused: 'Pausado', suppressed: 'Excluido', completed: 'Completado' }[channelStates[channel]?.status] || (channels.includes(channel) ? 'Listo' : 'Pendiente'));
  const allCards = [
    { label: 'Brief', state: campaign.campaignBrief?.pain ? 'Definido' : 'Pendiente', text: campaign.campaignBrief?.pain || 'Define solución, objetivo, dolor y CTA desde Editar diseño.', tone: 'slate' },
    { label: 'Email', state: stateLabel('email'), text: campaign.subject || 'Crea el email con IA o selecciona un flujo existente.', href: `/admin/marketing?${query}`, action: 'Abrir Email Studio', tone: 'violet' },
    { label: 'Landing', state: stateLabel('landing'), text: campaign.landingSlug ? `/soluciones/${campaign.landingSlug}` : 'Crea la landing y luego conéctala a esta campaña.', href: `/admin/marketing/landings?${query}`, action: 'Abrir Landing Studio', tone: 'orange' },
    { label: 'Meta y contenido', state: stateLabel('meta'), text: 'Crea contenido, video Veo y borrador de pauta desde el estudio de Meta.', href: `/admin/marketing/meta-ads?${query}`, action: 'Abrir Meta Ads', tone: 'sky' },
    { label: 'WhatsApp', state: stateLabel('whatsapp'), text: campaign.whatsappTemplate?.friendlyName ? `Plantilla: ${campaign.whatsappTemplate.friendlyName}` : 'Elige una plantilla aprobada de Twilio antes de activar este canal.', action: 'Configurar plantilla', onAction: onConfigureWhatsApp, tone: 'emerald' },
    { label: 'Messenger', state: stateLabel('messenger'), text: 'Prepara el anuncio o contenido que abre la conversación de Messenger.', href: `/admin/marketing/meta-ads?${query}`, action: 'Abrir Meta Ads', tone: 'sky' },
    { label: 'SMS', state: stateLabel('sms'), text: 'Canal reservado; se prepara cuando esté definido el mensaje y consentimiento.', tone: 'slate' },
  ];
  const cards = allCards.filter((card) => card.label === 'Brief' || (card.label === 'Email' && channels.includes('email')) || (card.label === 'Landing' && channels.includes('landing')) || (card.label === 'Meta y contenido' && channels.includes('meta')) || (card.label === 'WhatsApp' && channels.includes('whatsapp')) || (card.label === 'Messenger' && channels.includes('messenger')) || (card.label === 'SMS' && channels.includes('sms')));
  const tones = { slate: 'border-slate-200 bg-slate-50 text-slate-700', violet: 'border-violet-200 bg-violet-50 text-violet-800', orange: 'border-orange-200 bg-orange-50 text-orange-800', sky: 'border-sky-200 bg-sky-50 text-sky-800', emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800' };
  return <SectionCard title="Orquesta de campaña" subtitle="Una sola campaña coordina el brief, los activos y la distribución. Cada botón abre el estudio especializado con el contexto de esta campaña.">
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">{cards.map((card) => <article key={card.label} className={`rounded-xl border p-4 ${tones[card.tone]}`}><div className="flex items-start justify-between gap-2"><h3 className="text-sm font-bold">{card.label}</h3><span className="rounded-full bg-white/80 px-2 py-1 text-[10px] font-bold">{card.state}</span></div><p className="mt-3 min-h-[40px] text-xs leading-5">{card.text}</p>{card.href && <Link to={card.href} className="mt-4 inline-flex rounded-lg border border-current/20 bg-white px-3 py-2 text-xs font-bold">{card.action} →</Link>}{card.onAction && <button type="button" onClick={card.onAction} className="mt-4 inline-flex rounded-lg border border-current/20 bg-white px-3 py-2 text-xs font-bold">{card.action} →</button>}</article>)}</div>
    <div className="mt-4 border-t border-slate-200 pt-4"><div className="flex flex-wrap gap-2"><span className="text-xs font-bold text-slate-500">Canales:</span>{channels.map((channel) => <span key={channel} className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold uppercase text-slate-600">{channel} · {stateLabel(channel)}</span>)}{campaign.whatsappTemplate?.friendlyName && <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[10px] font-bold text-emerald-700">WhatsApp: {campaign.whatsappTemplate.friendlyName}</span>}</div>{channels.includes('whatsapp') && !campaign.whatsappTemplate?.friendlyName && <p className="mt-3 text-xs text-amber-700">WhatsApp está seleccionado: elige su plantilla aprobada al editar la campaña.</p>}{channels.includes('messenger') && <p className="mt-2 text-xs text-sky-700">Messenger está seleccionado: el anuncio o contenido Meta conserva esta campaña como origen.</p>}<p className="mt-3 text-xs text-slate-500">Journey: <strong className="text-slate-700">{campaign.journeyId || `${solution}-chat`}</strong> · Sale del proceso por: {(campaign.exitRules || ['respuesta', 'lead caliente', 'conversión', 'baja']).join(', ')}.</p></div>
  </SectionCard>;
}

function AddContactsPanel({ draft, setDraft, onCancel, onFile, preview, onPreview, onSubmit, saving }) {
  const change = (field, value) => setDraft((c) => ({ ...c, [field]: value }));
  const blocked = !draft.consentAllRows;
  return <SectionCard title="Agregar lista a esta campaña" subtitle="Los contactos entran directo a la cola de envío de esta campaña." action={<button type="button" onClick={onCancel} className="text-slate-400"><XMarkIcon className="h-5 w-5" /></button>}>
    <div className="flex flex-wrap items-center gap-3">
      <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50">
        <ArrowUpTrayIcon className="h-4 w-4" />Subir CSV
        <input type="file" accept=".csv,text/csv,text/plain" onChange={onFile} className="hidden" />
      </label>
      {draft.importFileName && <span className="text-xs text-slate-500">{draft.importFileName}</span>}
    </div>
    <Field label="Contactos" className="mt-3" hint="Acepta solo emails (uno por línea, con o sin encabezado) o CSV completo: email,nombre,empresa,teléfono,línea(erp/restaurante),consentimiento(si/no). Los duplicados se eliminan solos.">
      <textarea rows={5} value={draft.importCsv} onChange={(e) => change('importCsv', e.target.value)} placeholder={'juan@empresa.com\nmaria@negocio.com'} className={cx(inputCls, 'font-mono')} />
    </Field>
    <label className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs font-semibold text-amber-900">
      <input type="checkbox" className="mt-0.5" checked={draft.consentAllRows} onChange={(e) => change('consentAllRows', e.target.checked)} />
      Confirmo que esta lista tiene consentimiento de marketing por email. Si el CSV trae una columna de consentimiento por fila, se respeta esa; si no, aplica a toda la lista.
    </label>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <button type="button" onClick={onPreview} disabled={blocked} className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs font-bold text-slate-700 disabled:opacity-40">Validar lista</button>
      {preview && <p className="text-xs text-slate-500">
        {preview.totalRaw} filas leídas{preview.duplicatesRemoved ? ` · ${preview.duplicatesRemoved} duplicados eliminados` : ''}{preview.truncated ? ` · se recortó a las primeras ${IMPORT_ROW_LIMIT}` : ''} · {Object.entries(preview.counts || {}).map(([k, v]) => `${k}: ${v}`).join(' · ')}
      </p>}
    </div>
    <div className="mt-5 flex justify-end gap-2">
      <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600">Cancelar</button>
      <button type="button" onClick={onSubmit} disabled={saving || blocked} className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"><CheckIcon className="h-4 w-4" />{saving ? 'Agregando…' : 'Agregar a la campaña'}</button>
    </div>
  </SectionCard>;
}

function Stat({ label, value }) {
  return <div><p className="text-xs font-bold uppercase tracking-wide text-slate-400">{label}</p><p className="mt-1 text-xl font-black text-slate-900">{value ?? 0}</p></div>;
}

function AudienceEligibilitySummary({ summary = {} }) {
  const channels = Object.entries(summary);
  if (!channels.length) return null;
  const label = (state) => ({
    eligible: 'listos', template_required: 'requieren plantilla', needs_consent: 'sin consentimiento',
    needs_opt_in: 'sin opt-in', missing_contact: 'sin email', missing_phone: 'sin teléfono',
    missing_psid: 'sin Messenger', suppressed: 'excluidos', paused_human: 'atendidos por humano',
  }[state] || state);
  return <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">{channels.map(([channel, states]) => (
    <div key={channel} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{channel}</p>
      <p className="mt-1 text-xs leading-5 text-slate-700">{Object.entries(states).map(([state, count]) => `${count} ${label(state)}`).join(' · ')}</p>
    </div>
  ))}</div>;
}

// El estado del enrollment nunca retrocede (ver MARKETING_STATUS_RANK en el
// backend): quien está en 'clicked' ya pasó por 'delivered', pero no lo dice
// dos veces. Para un embudo real hay que sumar "este estado o más adelante".
function FunnelBreakdown({ funnel, audienceCount, onViewContacts }) {
  const pct = (n) => audienceCount ? `${Math.round((n / audienceCount) * 100)}%` : '—';
  const atLeastSent = (funnel.sent || 0) + (funnel.delivered || 0) + (funnel.opened || 0) + (funnel.clicked || 0) + (funnel.responded || 0) + (funnel.converted || 0);
  const atLeastDelivered = (funnel.delivered || 0) + (funnel.opened || 0) + (funnel.clicked || 0) + (funnel.responded || 0) + (funnel.converted || 0);
  const atLeastClicked = (funnel.clicked || 0) + (funnel.responded || 0) + (funnel.converted || 0);
  const completed = (funnel.responded || 0) + (funnel.converted || 0);
  const queued = (funnel.queued || 0) + (funnel.processing || 0);
  // 'clicked'/'opened' tal cual (sin sumar) SON justo "interactuó pero no
  // llegó más lejos" — si ya hubieran completado, el estado sería 'responded'.
  const clickedNoComplete = funnel.clicked || 0;
  const openedNoClick = funnel.opened || 0;
  const steps = [
    { label: 'Audiencia elegible', value: audienceCount, tone: 'bg-slate-400' },
    { label: 'Enviados (o más adelante)', value: atLeastSent, tone: 'bg-sky-400' },
    { label: 'Entregados (o más adelante)', value: atLeastDelivered, tone: 'bg-sky-500' },
    { label: 'Hicieron clic', value: atLeastClicked, tone: 'bg-violet-500' },
    { label: 'Completaron el cuestionario', value: completed, tone: 'bg-emerald-500' },
  ];
  return <div className="space-y-4">
    <div className="space-y-2">
      {steps.map((step) => <div key={step.label}>
        <div className="mb-1 flex items-center justify-between text-xs">
          <span className="font-semibold text-slate-600">{step.label}</span>
          <span className="text-slate-400">{step.value} · {pct(step.value)}</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className={`h-2 rounded-full ${step.tone}`} style={{ width: audienceCount ? `${Math.min(100, (step.value / audienceCount) * 100)}%` : '0%' }} />
        </div>
      </div>)}
    </div>
    <div className="rounded-lg border border-violet-200 bg-violet-50 px-4 py-3">
      <p className="text-xs font-bold uppercase tracking-wide text-violet-700">Interactuaron pero no completaron — tu audiencia de remarketing</p>
      <div className="mt-2 flex flex-wrap gap-2">
        <button type="button" onClick={() => onViewContacts('clicked', 'Hicieron clic, no completaron')} className="rounded-lg bg-violet-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-violet-700">
          Ver contactos que hicieron clic ({clickedNoComplete})
        </button>
        <button type="button" onClick={() => onViewContacts('opened', 'Abrieron, no hicieron clic')} className="rounded-lg border border-violet-300 bg-white px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-50">
          Ver los que solo abrieron ({openedNoClick})
        </button>
      </div>
    </div>
    <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-slate-100 pt-3 text-xs text-slate-500">
      <button type="button" onClick={() => onViewContacts('queued', 'En cola todavía')} className="hover:underline">En cola todavía: <strong className="text-slate-700">{queued}</strong></button>
      <button type="button" onClick={() => onViewContacts('suppressed', 'Rebotados/suprimidos')} className="hover:underline">Rebotados/suprimidos: <strong className="text-slate-700">{funnel.suppressed || 0}</strong></button>
      <button type="button" onClick={() => onViewContacts('failed', 'Fallidos')} className="hover:underline">Fallidos: <strong className="text-slate-700">{funnel.failed || 0}</strong></button>
    </div>
    {(funnel.opened || 0) === 0 && atLeastClicked > 0 && (
      <p className="text-[11px] italic text-slate-400">
        "Abiertos" suele quedar en 0 aunque haya clics — Apple Mail y Gmail bloquean el píxel de apertura por privacidad; el clic real sí se registra.
      </p>
    )}
  </div>;
}
